import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import SalovareDatePicker from '../common/SalovareDatePicker';
import { FIELD_PLACEHOLDER_COLOR, fieldLabelSx, nativeInputSx } from '../common/formFieldStyles';
import { IN_STORE_PAYMENT_METHODS, getStorePaymentMethodKey } from '../../constants/StorePaymentMethod';
import StockPurchaseLinesField, { createPurchaseLineFromProduct } from './StockPurchaseLinesField';
import { buildStockPurchasePayload, validateStockPurchase } from '../../utils/stockPurchaseValidation';
import { parseCalendarDateKey, toCalendarDateKey } from '../../utils/dateFormatUtils';

const buildInitial = (product) => ({
  supplierId: '',
  newSupplierName: '',
  invoiceReference: '',
  purchaseDate: toCalendarDateKey(new Date()),
  paymentMethod: 'Cash',
  notes: '',
  lines: [createPurchaseLineFromProduct(product)],
  idempotencyKey: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`,
});

const RecordStockPurchaseDialog = ({
  open,
  saving = false,
  products = [],
  suppliers = [],
  currency,
  canCreateProducts = false,
  initialProductId = null,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(buildInitial);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      const product = products.find((item) => String(item.id) === String(initialProductId));
      setForm(buildInitial(product));
      setErrors({});
    }
  }, [open, initialProductId, products]);

  const handleSubmit = () => {
    const todayKey = toCalendarDateKey(new Date());
    const nextErrors = validateStockPurchase(form, todayKey);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onSave(buildStockPurchasePayload(form));
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      title={t('stock.purchases.recordTitle')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button onClick={onClose} disabled={saving} color="inherit">{t('common.cancel')}</Button>
          <PrimaryActionButton id="record-purchase-save" onClick={handleSubmit} disabled={saving}>
            {saving ? t('common.saving') : t('stock.purchases.record')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box id="record-purchase-form" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <SalovareSelectField
          id="purchase-supplier"
          label={t('stock.purchases.supplier')}
          value={form.supplierId}
          displayEmpty
          onChange={(event) => setForm((previous) => ({ ...previous, supplierId: event.target.value }))}
          renderValue={(value) => {
            if (!value) {
              return <Typography component="span" sx={{ color: FIELD_PLACEHOLDER_COLOR }}>{t('stock.purchases.newSupplier')}</Typography>;
            }
            return suppliers.find((item) => item.id === value)?.name || value;
          }}
        >
          <MenuItem value="">{t('stock.purchases.newSupplier')}</MenuItem>
          {suppliers.filter((item) => !item.isArchived).map((supplier) => (
            <MenuItem key={supplier.id} value={supplier.id}>{supplier.name}</MenuItem>
          ))}
        </SalovareSelectField>
        {!form.supplierId ? (
          <Box>
            <Typography variant="subtitle2" sx={fieldLabelSx}>{t('stock.purchases.newSupplier')}</Typography>
            <Box
              component="input"
              id="purchase-new-supplier"
              aria-label={t('stock.purchases.newSupplier')}
              value={form.newSupplierName}
              onChange={(event) => setForm((previous) => ({ ...previous, newSupplierName: event.target.value }))}
              sx={nativeInputSx(false)}
            />
          </Box>
        ) : null}
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>{t('stock.purchases.invoice')}</Typography>
          <Box
            component="input"
            id="purchase-invoice"
            aria-label={t('stock.purchases.invoice')}
            value={form.invoiceReference}
            onChange={(event) => setForm((previous) => ({ ...previous, invoiceReference: event.target.value }))}
            sx={nativeInputSx(false)}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>{t('stock.purchases.date')} *</Typography>
          <SalovareDatePicker
            value={parseCalendarDateKey(form.purchaseDate)}
            disableFuture
            onChange={(date) => setForm((previous) => ({
              ...previous,
              purchaseDate: date ? toCalendarDateKey(date) : '',
            }))}
            error={Boolean(errors.purchaseDate)}
            helperText={errors.purchaseDate ? t(errors.purchaseDate) : undefined}
          />
        </Box>
        <SalovareSelectField
          id="purchase-payment-method"
          label={t('stock.purchases.paymentMethod')}
          required
          value={form.paymentMethod}
          error={Boolean(errors.paymentMethod)}
          helperText={errors.paymentMethod ? t(errors.paymentMethod) : undefined}
          onChange={(event) => setForm((previous) => ({ ...previous, paymentMethod: event.target.value }))}
        >
          {IN_STORE_PAYMENT_METHODS.map((method) => (
            <MenuItem key={method} value={method}>{t(`storePaymentMethods.${getStorePaymentMethodKey(method)}`)}</MenuItem>
          ))}
        </SalovareSelectField>
        <StockPurchaseLinesField
          lines={form.lines}
          products={products}
          currency={currency}
          errors={errors}
          canCreateProducts={canCreateProducts}
          onChange={(lines) => setForm((previous) => ({ ...previous, lines }))}
        />
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>{t('stock.purchases.notes')}</Typography>
          <Box
            component="textarea"
            id="purchase-notes"
            aria-label={t('stock.purchases.notes')}
            value={form.notes}
            onChange={(event) => setForm((previous) => ({ ...previous, notes: event.target.value }))}
            sx={nativeInputSx(false, true)}
          />
        </Box>
      </Box>
    </ScrollableDialog>
  );
};

export default RecordStockPurchaseDialog;
